import {
  InferenceDetailSource,
  type ApReportInference,
  type InferenceDetail,
  type InferenceDetailSource as InferenceDetailSourceType,
} from 'ap-openapi';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { type SelectProps } from '@moneyforward/ap-frontend-components';
import { DATE_FORMAT, format } from 'date-util';

export type { ApReportInference };

export type InferenceEntry = {
  source: NonNullable<InferenceDetailSourceType>;
  value: string;
};

export type ApReportInferencesMap = {
  apPayee: InferenceEntry | undefined;
  invoiceRegistrationNumber: InferenceEntry | undefined;
  dueDate: InferenceEntry | undefined;
  totalAmount: InferenceEntry | undefined;
};

export const SOURCE_LABEL_KEY: Record<
  NonNullable<InferenceDetailSourceType>,
  string
> = {
  [InferenceDetailSource.ocr]: 'report_inference_comparison_ocr_label',
  [InferenceDetailSource.ai]: 'report_inference_comparison_ai_label',
  [InferenceDetailSource.user]: 'report_inference_comparison_user_label',
  // Provisional: The wording for this key is set to "OCR" in ja/en.json.
  // Switching by source_detail is planned for Phase 2.
  // Re-implement separate labels for each source_detail (APWARRIOR-3251 & APWARRIOR-3658).
  [InferenceDetailSource.system]: 'report_inference_comparison_system_label',
};

const toInferenceEntry = (
  detail: InferenceDetail | undefined
): InferenceEntry | undefined => {
  if (!detail?.value || !detail.source) return undefined;
  return { source: detail.source, value: detail.value };
};

/**
 * 取引先は `account_number` の推論値（口座番号）で突き合わせる。
 * 口座番号として比較できない値（空白のみ / 非数値）はミスマッチ判定しない。
 */
export const toPayeeInferenceEntry = (
  detail: InferenceDetail | undefined
): InferenceEntry | undefined => {
  const entry = toInferenceEntry(detail);
  if (!entry) return undefined;

  const normalizedValue = entry.value.trim();
  if (normalizedValue === '' || Number.isNaN(Number(normalizedValue)))
    return undefined;

  return { source: entry.source, value: normalizedValue };
};

export const getInferredApPayeeId = (
  inferences: ApReportInference[]
): string | undefined => inferences[0]?.ap_payee?.value ?? undefined;

/**
 * to check if the inference.ap_payee's registered account number matches the OCR-read account number (inference.account_number.value).
 */
export const isInferredPayeeAccountNumberVerified = (
  registeredAccountNumber: string | null | undefined,
  inferredAccountNumber: string | null | undefined
): boolean => {
  if (!registeredAccountNumber || !inferredAccountNumber) return false;

  return registeredAccountNumber === inferredAccountNumber;
};

export const toApReportInferencesMap = (
  inferences: ApReportInference[]
): ApReportInferencesMap => {
  const latestInference = inferences[0];
  return {
    apPayee: toPayeeInferenceEntry(latestInference?.account_number),
    invoiceRegistrationNumber: toInferenceEntry(
      latestInference?.invoice_registration_number
    ),
    dueDate: toInferenceEntry(latestInference?.due_date),
    totalAmount: toInferenceEntry(latestInference?.total_amount),
  };
};

// toComparable functions; will finalize and update the specifications after the actual data (actual formats of form values, drawer values, and inference values) becomes available.
export const toComparableDueDate = (
  v: string | Dayjs | null | undefined
): string | null => {
  if (v === null || v === undefined) return null;
  const d = dayjs(v);
  return d.isValid()
    ? d.format('YYYY-MM-DD')
    : typeof v === 'string'
    ? v
    : null;
};

export const toComparableTotalAmount = (
  v: string | null | undefined
): string | null => {
  if (v === null || v === undefined) return null;
  const numeric = v.replace(/[^\d.-]/g, '');
  if (numeric === '') return v;

  const n = Number(numeric);
  return Number.isNaN(n) ? v : String(n);
};

// Normalizes the inference value the same way as the current value, to avoid
// false positives from notation differences alone (e.g. "1000.0" vs "1000").
export const toNormalizedTotalAmountInference = (
  inference: InferenceEntry | undefined
): InferenceEntry | undefined =>
  inference && {
    ...inference,
    value: toComparableTotalAmount(inference.value) ?? inference.value,
  };

export type MismatchValueKind = 'amount' | 'date' | 'text';

const toMismatchDisplayValue = (
  value: string,
  kind: MismatchValueKind
): string => {
  switch (kind) {
    case 'amount':
      return toCommaSeparated(value);
    case 'date':
      return format(value, DATE_FORMAT) || value;
    case 'text':
      return value;
  }
};

export const toMismatchMessage = (
  mismatch: InferenceEntry | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  kind: MismatchValueKind
): string | undefined => {
  if (!mismatch) return undefined;

  const messageKey =
    kind === 'amount'
      ? 'report_inference_comparison_mismatch_amount_message'
      : 'report_inference_comparison_mismatch_message';

  return t(messageKey, {
    label: t(SOURCE_LABEL_KEY[mismatch.source]),
    value: toMismatchDisplayValue(mismatch.value, kind),
  });
};

export const toMismatchPrefixIndicator = (
  mismatch: InferenceEntry | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  kind: MismatchValueKind
): SelectProps['prefixIndicator'] => {
  const tooltip = toMismatchMessage(mismatch, t, kind);
  if (!tooltip) return undefined;

  return { type: 'alert', tooltip };
};

export const getMismatch = (
  inference: InferenceEntry | undefined,
  comparableCurrentValue: string | null | undefined
): InferenceEntry | null => {
  if (!inference) return null;

  if (inference.value === comparableCurrentValue) return null;

  return { source: inference.source, value: inference.value };
};
